import {
  useCallback,
  useState,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogCloseButton,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import { UnsavedChangesConfirmDialog } from '@/components/blocks/feedback/UnsavedChangesConfirmDialog';

type EntityDialogSize = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<EntityDialogSize, string> = {
  sm: 'max-w-[520px]',
  md: 'max-w-[720px]',
  lg: 'max-w-[840px]',
};

export interface EntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  contextLine?: string;
  size?: EntityDialogSize;
  isDirty?: boolean;
  isSaving?: boolean;
  footer?: ReactNode;
  dangerZone?: ReactNode;
  banner?: ReactNode;
  summary?: ReactNode;
  formId?: string;
  submitOnMetaEnter?: boolean;
  children: ReactNode;
}

export function EntityDialog({
  open,
  onOpenChange,
  title,
  description,
  contextLine,
  size = 'md',
  isDirty = false,
  isSaving = false,
  footer,
  dangerZone,
  banner,
  summary,
  formId,
  submitOnMetaEnter = true,
  children,
}: EntityDialogProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setConfirmOpen(true);
      return;
    }
    onOpenChange(false);
  }, [isDirty, isSaving, onOpenChange]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true);
        return;
      }
      requestClose();
    },
    [onOpenChange, requestClose]
  );

  const handleEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      requestClose();
    },
    [requestClose]
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (!submitOnMetaEnter || !formId) return;
      const isMetaEnter = (event.metaKey || event.ctrlKey) && event.key === 'Enter';
      if (!isMetaEnter) return;
      const form = document.getElementById(formId) as HTMLFormElement | null;
      form?.requestSubmit();
    },
    [formId, submitOnMetaEnter]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn(
            'w-full rounded-lg border border-[var(--border-default)] bg-surface p-0 shadow-lg',
            SIZE_CLASS[size]
          )}
          onEscapeKeyDown={handleEscapeKeyDown}
          onKeyDown={handleKeyDown}
        >
          <DialogHeader>
            <div className="space-y-1">
              <DialogTitle className="text-lg font-semibold normal-case tracking-normal text-text-primary">
                {title}
              </DialogTitle>
              {description ? (
                <DialogDescription className="text-sm text-text-secondary">
                  {description}
                </DialogDescription>
              ) : null}
              {contextLine ? <p className="text-xs text-text-secondary">{contextLine}</p> : null}
              <p className="text-xs text-text-secondary">
                I campi contrassegnati con * sono obbligatori.
              </p>
            </div>
            <DialogCloseButton
              disabled={isSaving}
              className="text-text-secondary hover:text-text-primary"
              aria-disabled={isSaving}
            />
          </DialogHeader>

          <DialogBody className="px-6 pb-6 pt-5">
            <div className="space-y-4">
              {banner}
              {summary}
              {children}
              {dangerZone}
            </div>
          </DialogBody>

          {footer ? (
            <DialogFooter className="border-t border-[var(--border-subtle)] bg-surface px-6 py-4">
              {footer}
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>

      <UnsavedChangesConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirmExit={() => {
          setConfirmOpen(false);
          onOpenChange(false);
        }}
        onContinue={() => setConfirmOpen(false)}
        isSaving={isSaving}
      />
    </>
  );
}
